import React, { act } from 'react';
import { Button, Box, Stack, Typography, Checkbox } from '@mui/material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';
import { DebounceInput } from 'react-debounce-input';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { SmallFormTextField } from '../../../../../components/small-fields/SmallFormTextField';
import { AlertDialogContainer } from '../../../../../components/others/AlertDialogContainer';
import { submitPackageAction } from '../../../../../services/request';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import { useSelector } from 'react-redux';
import { SmallFormDateField } from '../../../../../components/small-fields/SmallFormDateField';
import { formatDate } from '../../../../../services/functions';

export const PackageForm = props => {
  const { data, submitCallback, handleClose, choices } = props;
  const { loading, mutate } = useMutation(submitPackageAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

  const validationSchema = Yup.object({
    name: Yup.string().required('To pole nie może być puste'),
    period: Yup.string().required('To pole nie może być puste'),
    gross_value: Yup.string().required('To pole nie może być puste'),
    activities: Yup.array()
      .of(Yup.string().required())
      .min(1, 'Wybierz przynajmniej jedną aktywność')
      .required('To pole nie może być puste'),
    resorts: Yup.array()
      .of(Yup.string().required())
      .min(1, 'Wybierz przynajmniej jeden ośrodek')
      .required('To pole nie może być puste'),
    calendar_shortcut: Yup.string().required('To pole nie może być puste')
  });

  const initialValues = {
    name: data ? data.name || '' : '',
    resorts: data ? data.resorts : [],
    period: data ? data.period : '',
    activities: data ? data.activities : [],
    gross_value: data ? data.gross_value : '0.00',
    calendar_shortcut: data ? data.calendar_shortcut : ''
  };

  const handleSubmit = async values => {
    const { error, payload } = await mutate(values, data ? 'PUT' : 'POST', data ? data.id : undefined);
    if (!error) {
      submitCallback(payload, data ? 'edit' : 'created', 'success', data ? 'Zaktualizowano pakiet' : 'Dodano nowy pakiet', '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle('Nie udało się dodać nowego pakietu');
      setMessage('');
      setShowAlert(true);
    }
  };

  const handleDelete = async () => {
    const { error, payload } = await mutate([], 'DELETE', data.id);
    if (!error) {
      submitCallback(data, 'delete', 'success', `Usunięto pakiet ${data.name}`, '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle(`Nie udało się usunąć pakietu ${data.name}`);
      setMessage(payload);
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  return (
    <>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <form onSubmit={formik.handleSubmit} style={{ textAlign: 'center' }}>
        <Box display="flex" flexDirection="column" mt={1} p={1} ml="5%" mr="5%">
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Nazwa</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="name"
              id="name"
              type="text"
              placeholder="nazwa pakietu"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              debounceTimeout={100}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Termin</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              id="period"
              name="period"
              error={formik.touched.period && Boolean(formik.errors.period)}
              helperText={formik.touched.period && formik.errors.period}
              options={choices.periods}
              value={formik.values.period}
              onChange={formik.handleChange} />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Aktywności</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              multiple
              id="activities"
              name="activities"
              error={formik.touched.activities && Boolean(formik.errors.activities)}
              helperText={formik.touched.activities && formik.errors.activities}
              options={choices.activities}
              value={formik.values.activities}
              onChange={formik.handleChange} />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Ośrodki</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              multiple
              id="resorts"
              name="resorts"
              error={formik.touched.resorts && Boolean(formik.errors.resorts)}
              helperText={formik.touched.resorts && formik.errors.resorts}
              options={choices.resorts}
              value={formik.values.resorts}
              onChange={formik.handleChange} />
          </Stack>
          {/* <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Sezon</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              id="season"
              name="season"
              error={formik.touched.season && Boolean(formik.errors.season)}
              helperText={formik.touched.season && formik.errors.season}
              options={choices.seasons}
              value={formik.values.season}
              onChange={formik.handleChange} />
          </Stack> */}
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Wartość brutto (zł per uczestnik)</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="gross_value"
              id="gross_value"
              type="text"
              placeholder="wartość pakietu (zł per uczestnik)"
              value={formik.values.gross_value}
              onChange={formik.handleChange}
              error={formik.touched.gross_value && Boolean(formik.errors.gross_value)}
              helperText={formik.touched.gross_value && formik.errors.gross_value}
              debounceTimeout={100}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Skrót pakietu</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="calendar_shortcut"
              id="calendar_shortcut"
              type="text"
              placeholder="skrót pakietu"
              value={formik.values.calendar_shortcut}
              onChange={formik.handleChange}
              error={formik.touched.calendar_shortcut && Boolean(formik.errors.calendar_shortcut)}
              helperText={formik.touched.calendar_shortcut && formik.errors.calendar_shortcut}
              debounceTimeout={100}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
        </Box>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          sx={{ mt: 3 }}
          disabled={loading}
        >
          <AddIcon />
          Zapisz
        </Button>
        { data
          ? <Button
            color="error"
            variant="contained"
            sx={{ mt: 3, ml: 4 }}
            onClick={() => {
              setConfirmDeleteOpen(true);
            }}
          >
            <DeleteIcon />
          Usuń
          </Button>
          : null}
      </form>
      <ConfirmDialog
        title="Usunąć pakiet?"
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={handleDelete}
        disabled={loading}
      >
        Czy na pewno chcesz usunąć ten pakiet?
      </ConfirmDialog>
    </>

  );
};
